import React, { useState, useEffect } from 'react';
import { Logo, LogoMark, BRAND_NAME } from '../components/Logo';
import { useAuth } from '../context/AuthContext';
import { setDemoSessionToken } from '../utils/demoSession';
import { captureSource } from '../utils/requestAccessSource';
import { RequestAccessModal } from '../components/RequestAccessModal';
import { trackEvent } from '../utils/clarity';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? '/api';

export default function Landing() {
  const { login } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoError, setDemoError] = useState<string | null>(null);
  // ?requestAccess=1 auto-opens the form — used by the demo-mode exit flow (Layout.tsx,
  // DemoSignInModal.tsx) so clicking "Request access" from inside the demo lands here
  // with the form already open, instead of a bare Landing page.
  const [requestAccessOpen, setRequestAccessOpen] = useState(
    () => new URLSearchParams(window.location.search).get('requestAccess') === '1'
  );

  useEffect(() => {
    captureSource();
    trackEvent('landing_view');
  }, []);

  // No credentials sent or required — POST /api/demo/session is the one endpoint that
  // takes none. The token it returns is what actually grants demo access on every
  // subsequent request (see flowClient's X-Demo-Session header); nothing client-side is
  // ever trusted as the "is this a demo" signal.
  const startDemo = async () => {
    trackEvent('try_sample_clicked');
    setDemoLoading(true);
    setDemoError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/demo/session`, { method: 'POST' });
      if (!res.ok) throw new Error('Could not start the demo. Please try again.');
      const data = await res.json();
      setDemoSessionToken(data.token);
      window.location.href = '/project/DEMO-001/dashboard';
    } catch (err) {
      setDemoError(err instanceof Error ? err.message : 'Could not start the demo.');
      setDemoLoading(false);
    }
  };

  return (
    <>
      <style>{`
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  /* Colours come from the shared Flotillo tokens (tailwind.config.js → :root). */
  --radius: 10px;
  --radius-sm: 6px;
  --radius-pill: 20px;
}

body {
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 15px;
  color: var(--text);
  background: var(--ground);
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}

a { text-decoration: none; color: inherit; }
h1, h2, h3, .hero-h1 { font-family: 'Manrope', 'DM Sans', sans-serif; }

.nav {
  background: var(--ink);
  padding: 0 40px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
}
.nav-logo {
  color: white;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 8px;
}
.nav-logo-icon {
  width: 24px;
  height: 24px;
  background: rgba(255,255,255,0.15);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.nav-links {
  display: flex;
  align-items: center;
  gap: 28px;
}
.nav-links a {
  color: rgba(255,255,255,0.65);
  font-size: 13px;
  font-weight: 400;
  transition: color 0.15s;
}
.nav-links a:hover { color: white; }
.nav-right { display: flex; align-items: center; gap: 10px; }
.btn-ghost-white {
  color: rgba(255,255,255,0.8);
  font-size: 13px;
  padding: 7px 14px;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: all 0.15s;
  background: transparent;
  font-family: inherit;
}
.btn-ghost-white:hover { background: rgba(255,255,255,0.08); color: white; }
.btn-white {
  background: white;
  color: var(--ink);
  font-size: 13px;
  font-weight: 600;
  padding: 7px 16px;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: opacity 0.15s;
}
.btn-white:hover { opacity: 0.9; }

/* ── Hero ── */
.hero {
  background: var(--ink);
  padding: 64px 40px 56px;
  text-align: center;
}
.hero-h1 {
  font-size: 40px;
  font-weight: 600;
  color: white;
  line-height: 1.2;
  letter-spacing: -0.02em;
  max-width: 680px;
  margin: 0 auto 16px;
}
.hero-sub {
  font-size: 16px;
  color: rgba(255,255,255,0.65);
  max-width: 560px;
  margin: 0 auto 32px;
  line-height: 1.7;
}
.hero-btns {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 18px;
  flex-wrap: wrap;
}
.btn-primary {
  background: var(--accent);
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: var(--radius-sm);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: opacity 0.15s;
  border: none;
}
.btn-primary:hover { opacity: 0.9; }
.btn-secondary {
  background: rgba(255,255,255,0.08);
  color: white;
  font-size: 14px;
  font-weight: 500;
  padding: 12px 24px;
  border-radius: var(--radius-sm);
  border: 1px solid rgba(255,255,255,0.15);
  cursor: pointer;
  transition: background 0.15s;
}
.btn-secondary:hover { background: rgba(255,255,255,0.12); }
.hero-note { font-size: 12px; color: rgba(255,255,255,0.45); margin-bottom: 40px; }
/* Caption directly under the hero demo button — reads as a note, not a second CTA. */
.hero-caption { font-size: 12.5px; color: rgba(255,255,255,0.6); margin: -10px 0 18px; }

/* ── Browser-frame mockups (reused across hero + beats) ── */
.frame {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.14);
  text-align: left;
}
.frame-chrome {
  background: var(--border);
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.frame-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.frame-dot.red { background: #ED6A5E; }
.frame-dot.amber { background: #F4BF4F; }
.frame-dot.green { background: #61C454; }
.frame-url {
  margin-left: 8px;
  background: var(--surface);
  border-radius: 5px;
  padding: 3px 12px;
  font-size: 10px;
  color: var(--faint);
  flex: 1;
  min-width: 0;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.app-nav-mini {
  background: var(--ink);
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.app-nav-mini-brand {
  color: white;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.app-nav-mini-brand-icon {
  width: 16px;
  height: 16px;
  background: rgba(255,255,255,0.15);
  border-radius: 4px;
}
.app-nav-mini-tabs { display: flex; gap: 2px; overflow-x: auto; min-width: 0; }
.app-nav-mini-tab {
  font-size: 10.5px;
  color: rgba(255,255,255,0.5);
  padding: 5px 9px;
  border-radius: 6px;
  white-space: nowrap;
}
.app-nav-mini-tab.active { background: rgba(255,255,255,0.14); color: white; font-weight: 600; }

.hero-visual { max-width: 780px; margin: 0 auto; }

.mini-table { width: 100%; border-collapse: collapse; }
.mini-table th {
  background: var(--ground);
  padding: 8px 14px;
  font-size: 10px;
  font-weight: 600;
  color: var(--faint);
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
}
.mini-table td {
  padding: 9px 14px;
  font-size: 12.5px;
  color: var(--text);
  border-bottom: 1px solid var(--border);
}
.mini-table tr:last-child td { border-bottom: none; }
.mini-table td.secondary { color: var(--muted); }
.mini-scroll { overflow-x: auto; }

.badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  padding: 3px 10px;
  border-radius: var(--radius-pill);
  white-space: nowrap;
}
.badge-red { background: var(--crit-bg); color: var(--crit-text); }
.badge-amber { background: var(--watch-bg); color: var(--watch-text); }
.badge-green { background: var(--ok-bg); color: var(--ok-text); }

.ai-teaser {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid var(--border);
  background: var(--ground);
}
.ai-teaser-icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: var(--tint);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ai-teaser-icon svg { width: 14px; height: 14px; }
.ai-teaser-label { font-size: 10px; font-weight: 700; color: var(--faint); text-transform: uppercase; letter-spacing: 0.05em; }
.ai-teaser-text { font-size: 12.5px; color: var(--text); }

/* ── Generic section shell ── */
.section {
  padding: 64px 40px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}
.section.alt { background: var(--ground); }
.section.compact { padding: 36px 40px; }
.section-eyebrow {
  font-size: 11px;
  font-weight: 600;
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-align: center;
  margin-bottom: 10px;
}
.section-title {
  font-size: 26px;
  font-weight: 600;
  text-align: center;
  max-width: 640px;
  margin: 0 auto 20px;
  line-height: 1.3;
  letter-spacing: -0.01em;
}
.section-title.tight { margin-bottom: 44px; }
.section-lede {
  font-size: 14px;
  color: var(--muted);
  text-align: center;
  max-width: 620px;
  margin: 0 auto 44px;
  line-height: 1.7;
}

/* ── Workflow (the P6 loop) ── */
.workflow-diagram {
  display: grid;
  grid-template-columns: 1fr auto 1fr auto 1fr;
  align-items: stretch;
  gap: 0 4px;
  max-width: 1040px;
  margin: 0 auto 28px;
}
.workflow-cell {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 26px 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.workflow-cell.highlight {
  background: var(--tint);
  border-color: var(--border);
  box-shadow: 0 4px 16px var(--tint);
}
.workflow-role {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--faint);
}
.workflow-cell.highlight .workflow-role { color: var(--accent); }
.workflow-heading { font-size: 15px; font-weight: 600; color: var(--text); }
.workflow-body { font-size: 13px; color: var(--muted); line-height: 1.6; }
.workflow-list { list-style: none; display: flex; flex-direction: column; gap: 7px; margin-top: 2px; }
.workflow-list li {
  font-size: 12.5px;
  color: var(--text);
  display: flex;
  gap: 8px;
  align-items: flex-start;
  line-height: 1.4;
}
.workflow-list li::before {
  content: '';
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--accent);
  margin-top: 6px;
  flex-shrink: 0;
}
.workflow-arrow-col {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  padding: 0 2px;
}
.workflow-note {
  text-align: center;
  font-size: 13.5px;
  color: var(--muted);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.7;
}

/* ── Outcomes strip ── */
.outcomes-strip {
  display: flex;
  justify-content: center;
  gap: 44px;
  flex-wrap: wrap;
  max-width: 900px;
  margin: 0 auto;
}
.outcome-item { display: flex; align-items: center; gap: 10px; }
.outcome-num {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: var(--tint);
  color: var(--accent);
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.outcome-label { font-size: 14px; font-weight: 600; color: var(--text); }

/* ── Card grids (problem cards, who-it's-for cards) ── */
.card-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 960px;
  margin: 0 auto;
}
.info-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.info-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.info-icon svg { width: 17px; height: 17px; }
.info-icon.amber { background: var(--watch-bg); color: var(--watch-text); }
.info-icon.green { background: var(--tint); color: var(--accent); }
.info-title { font-size: 14.5px; font-weight: 600; color: var(--text); }
.info-body { font-size: 13px; color: var(--muted); line-height: 1.65; }

/* ── Alternating beats ── */
.beat-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  max-width: 1040px;
  margin: 0 auto;
  align-items: center;
}
.beat-row.reverse .beat-text { order: 2; }
.beat-row.reverse .beat-visual { order: 1; }
.beat-text, .beat-visual { min-width: 0; }
.beat-eyebrow {
  font-size: 11px;
  font-weight: 600;
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 10px;
}
.beat-title { font-size: 23px; font-weight: 600; line-height: 1.3; letter-spacing: -0.01em; margin-bottom: 14px; }
.beat-body { font-size: 14px; color: var(--muted); line-height: 1.75; margin-bottom: 18px; }
.beat-points { display: flex; flex-direction: column; gap: 10px; }
.beat-point { display: flex; gap: 10px; align-items: flex-start; font-size: 13px; color: var(--muted); }
.beat-point-check {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--tint);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}
.beat-point-check svg { width: 9px; height: 9px; }
.beat-visual-inner { padding: 16px; }

/* Impact preview visual */
.impact-panel { padding: 16px; }
.impact-header { font-size: 11px; font-weight: 700; color: var(--faint); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 12px; }
.impact-change { font-size: 12.5px; color: var(--muted); margin-bottom: 14px; }
.impact-change b { color: var(--text); font-weight: 600; }
.impact-date-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: var(--ground);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 12px 14px;
  margin-bottom: 14px;
}
.impact-date-label { font-size: 10.5px; color: var(--faint); }
.impact-date-value { font-size: 13.5px; font-weight: 600; color: var(--text); margin-top: 2px; }
.impact-date-value.muted { color: var(--muted); font-weight: 500; }
.impact-list { display: flex; flex-direction: column; gap: 8px; }
.impact-list-title { font-size: 10.5px; font-weight: 700; color: var(--faint); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px; }
.impact-list-item { font-size: 12.5px; color: var(--muted); display: flex; gap: 8px; align-items: flex-start; }
.impact-list-item::before { content: '•'; color: var(--crit-text); font-weight: 700; }

/* Ask AI chat visual */
.chat-panel { padding: 18px 16px; display: flex; flex-direction: column; gap: 12px; }
.chat-bubble { max-width: 82%; font-size: 13px; line-height: 1.6; padding: 10px 14px; border-radius: 12px; }
.chat-bubble.user {
  align-self: flex-end;
  background: var(--ink);
  color: white;
  border-bottom-right-radius: 3px;
}
.chat-bubble.answer {
  align-self: flex-start;
  background: var(--ground);
  border: 1px solid var(--border);
  color: var(--text);
  border-bottom-left-radius: 3px;
}
.chat-bubble.answer b { color: var(--accent); }

/* Report visual (reused inside a beat) */
.report-demo {
  background: var(--surface);
}
.report-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border);
  background: var(--ground);
}
.report-title { font-size: 12.5px; font-weight: 600; color: var(--text); }
.report-date { font-size: 10.5px; color: var(--faint); }
.report-ai-tag {
  font-size: 10.5px;
  font-weight: 500;
  background: var(--tint);
  color: var(--accent);
  padding: 3px 10px;
  border-radius: var(--radius-pill);
  flex-shrink: 0;
}
.report-body {
  padding: 16px;
  font-size: 12.5px;
  color: var(--muted);
  line-height: 1.75;
  border-left: 3px solid var(--ink);
  margin: 14px 16px;
}
.report-actions { display: flex; gap: 8px; padding: 0 16px 16px; flex-wrap: wrap; }
.report-btn {
  font-size: 11.5px;
  font-weight: 500;
  padding: 7px 12px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  color: var(--muted);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: var(--surface);
}
.report-btn.primary { background: var(--ink); color: white; border-color: var(--ink); }

/* ── Works alongside P6 (compressed) ── */
.approach-block { max-width: 620px; margin: 0 auto; text-align: center; }
.approach-block p { font-size: 14px; color: var(--muted); line-height: 1.7; margin-bottom: 18px; }
.approach-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: var(--radius-pill);
  background: var(--tint);
  color: var(--accent);
}

/* ── Final CTA ── */
.final {
  background: var(--ink);
  padding: 72px 40px;
  text-align: center;
}
.final-title {
  font-size: 30px;
  font-weight: 600;
  color: white;
  line-height: 1.3;
  letter-spacing: -0.015em;
  max-width: 520px;
  margin: 0 auto 14px;
}
.final-sub {
  font-size: 14px;
  color: rgba(255,255,255,0.6);
  max-width: 440px;
  margin: 0 auto 32px;
  line-height: 1.7;
}
.final-note { font-size: 12px; color: rgba(255,255,255,0.35); margin-top: 18px; }

.footer {
  background: var(--ink);
  padding: 24px 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}
.footer-logo { color: rgba(255,255,255,0.4); font-size: 13px; font-weight: 500; }
.footer-links { display: flex; gap: 20px; }
.footer-links a { color: rgba(255,255,255,0.3); font-size: 12px; }
.footer-links a:hover { color: rgba(255,255,255,0.6); }

svg { display: inline-block; vertical-align: middle; }

/* ── Responsive ── */
@media (max-width: 900px) {
  .card-grid { grid-template-columns: repeat(2, 1fr); }
  .beat-row {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  .beat-row.reverse .beat-text,
  .beat-row.reverse .beat-visual,
  .beat-row .beat-text,
  .beat-row .beat-visual {
    order: 0;
  }
}

@media (max-width: 840px) {
  .workflow-diagram { grid-template-columns: 1fr; gap: 4px; }
  .workflow-arrow-col { padding: 2px 0; }
  .workflow-arrow-col svg { transform: rotate(90deg); }
}

@media (max-width: 700px) {
  .nav { padding: 0 20px; }
  .nav-links { display: none; }
  .hero { padding: 44px 20px 36px; }
  .hero-h1 { font-size: 28px; }
  .section { padding: 40px 20px; }
  .section.compact { padding: 28px 20px; }
  .card-grid { grid-template-columns: 1fr; }
  .final { padding: 48px 20px; }
  .final-title { font-size: 22px; }
  .outcomes-strip { gap: 24px; }
}
      `}</style>

      {/* NAV */}
      <nav className="nav">
        <div className="nav-logo" aria-label={BRAND_NAME}>
          <Logo variant="onDark" wordmarkSize={40} />
        </div>
        <div className="nav-links">
          <a href="#workflow">The workflow</a>
          <a href="#lookahead">Product tour</a>
          <a href="#for">Who it's for</a>
          <a href="#approach">Works with P6</a>
        </div>
        <div className="nav-right">
          <button className="btn-ghost-white" onClick={login}>Sign in</button>
          <button className="btn-white" onClick={() => { trackEvent('request_access_clicked'); setRequestAccessOpen(true); }}>Request access</button>
        </div>
      </nav>

      {/* 1 — HERO */}
      <section className="hero">
        <h1 className="hero-h1">Your P6 schedule, explained — and made actionable.</h1>
        <p className="hero-sub">
          Start with your scheduler's P6 schedule. Keep it updated, understand what's at risk, test changes before you make them, and send approved updates back to P6.
        </p>
        <div className="hero-btns">
          <button onClick={startDemo} disabled={demoLoading} className="btn-primary">
            {demoLoading ? 'Loading sample schedule…' : 'Explore the live demo'}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
        <p className="hero-caption">Opens a sample project. No upload or signup.</p>
        {demoError && <p className="hero-note" style={{color:'var(--crit-bg)'}}>{demoError}</p>}
        <p className="hero-note">Works with P6 XER + Excel · No P6 license required for PMs</p>

        <div className="hero-visual">
          <div className="frame">
            <div className="frame-chrome">
              <div className="frame-dot red"></div>
              <div className="frame-dot amber"></div>
              <div className="frame-dot green"></div>
              <div className="frame-url">{BRAND_NAME} / project / lookahead</div>
            </div>
            <div className="app-nav-mini">
              <div className="app-nav-mini-brand">
                <LogoMark variant="onDark" height={12} />
                {BRAND_NAME}
              </div>
              <div className="app-nav-mini-tabs">
                <div className="app-nav-mini-tab active">Lookahead</div>
                <div className="app-nav-mini-tab">Ask</div>
                <div className="app-nav-mini-tab">Reports</div>
                <div className="app-nav-mini-tab">Upload</div>
              </div>
            </div>
            <div className="mini-scroll">
              <table className="mini-table">
                <thead>
                  <tr>
                    <th>Activity</th>
                    <th>Start</th>
                    <th>Finish</th>
                    <th>Float</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>As-Builts Drawings</td>
                    <td className="secondary">Feb 16</td>
                    <td className="secondary">Apr 29</td>
                    <td className="secondary">0d</td>
                    <td><span className="badge badge-red">Critical</span></td>
                  </tr>
                  <tr>
                    <td>Final Acceptance &amp; Handover</td>
                    <td className="secondary">Apr 29</td>
                    <td className="secondary">May 13</td>
                    <td className="secondary">0d</td>
                    <td><span className="badge badge-red">Critical</span></td>
                  </tr>
                  <tr>
                    <td>Fire Alarm Commissioning</td>
                    <td className="secondary">Jun 30</td>
                    <td className="secondary">Jul 8</td>
                    <td className="secondary">3d</td>
                    <td><span className="badge badge-amber">On watch</span></td>
                  </tr>
                  <tr>
                    <td>Interior Punch List</td>
                    <td className="secondary">Jul 2</td>
                    <td className="secondary">Jul 11</td>
                    <td className="secondary">21d</td>
                    <td><span className="badge badge-green">On track</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="ai-teaser">
              <div className="ai-teaser-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </div>
              <div>
                <div className="ai-teaser-label">{BRAND_NAME} AI</div>
                <div className="ai-teaser-text">"What's blocking completion?" — answered instantly from your live schedule.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2 — THE WORKFLOW */}
      <section className="section" id="workflow">
        <div className="section-eyebrow">The workflow</div>
        <h2 className="section-title tight">From P6 baseline to project updates — without putting every PM in P6.</h2>
        <div className="workflow-diagram">
          <div className="workflow-cell">
            <div className="workflow-role">Scheduler</div>
            <div className="workflow-heading">Creates the baseline</div>
            <div className="workflow-body">Your scheduler builds and exports the official schedule in P6, same as always.</div>
          </div>
          <div className="workflow-arrow-col">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6"/></svg>
          </div>
          <div className="workflow-cell highlight">
            <div className="workflow-role">PM — in {BRAND_NAME}</div>
            <div className="workflow-heading">Maintains &amp; analyzes</div>
            <ul className="workflow-list">
              <li>Update actual dates</li>
              <li>Review the weekly lookahead</li>
              <li>Identify risk</li>
              <li>Ask questions</li>
              <li>Run what-if analysis</li>
              <li>Generate reports</li>
            </ul>
          </div>
          <div className="workflow-arrow-col">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6"/></svg>
          </div>
          <div className="workflow-cell">
            <div className="workflow-role">Scheduler</div>
            <div className="workflow-heading">Imports approved changes</div>
            <div className="workflow-body">Approved updates export cleanly for your scheduler to bring back into P6.</div>
          </div>
        </div>
        <p className="workflow-note">
          Your scheduler establishes the baseline. Your PM keeps the schedule current as work happens. Approved changes can then be exported for import back into P6.
        </p>
      </section>

      {/* 3 — THREE OUTCOMES */}
      <section className="section alt compact">
        <div className="outcomes-strip">
          <div className="outcome-item">
            <div className="outcome-num">1</div>
            <div className="outcome-label">See what changed</div>
          </div>
          <div className="outcome-item">
            <div className="outcome-num">2</div>
            <div className="outcome-label">Know what's at risk</div>
          </div>
          <div className="outcome-item">
            <div className="outcome-num">3</div>
            <div className="outcome-label">Make it actionable</div>
          </div>
        </div>
      </section>

      {/* 4 — THE PROBLEM */}
      <section className="section">
        <h2 className="section-title tight">P6 is powerful. But the people running the project shouldn't have to live in it.</h2>
        <div className="card-grid">
          <div className="info-card">
            <div className="info-icon amber">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
            </div>
            <div className="info-title">The scheduler is the bottleneck</div>
            <div className="info-body">Every question about the schedule routes through one person and one P6 file. If they're busy, everyone else waits.</div>
          </div>
          <div className="info-card">
            <div className="info-icon amber">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M12 9v3M12 15h.01"/></svg>
            </div>
            <div className="info-title">Risk builds silently</div>
            <div className="info-body">Float erodes and activities slip between updates, and nobody notices until the report lands — or the deadline does.</div>
          </div>
          <div className="info-card">
            <div className="info-icon amber">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12h5M17 12h5M12 2v5M12 17v5"/><circle cx="12" cy="12" r="4"/></svg>
            </div>
            <div className="info-title">Changes get made blind</div>
            <div className="info-body">A shifted date, an added day of float — the real impact on completion isn't visible until after it's already been sent back to P6.</div>
          </div>
        </div>
      </section>

      {/* 5 — LOOKAHEAD BEAT */}
      <section className="section alt" id="lookahead">
        <div className="beat-row">
          <div className="beat-text">
            <div className="beat-eyebrow">Weekly Lookahead</div>
            <h3 className="beat-title">See what deserves your attention this week.</h3>
            <p className="beat-body">A P6 schedule can run to hundreds of activities. Flotillo cuts straight to what's starting, ongoing, and finishing in the weeks ahead — so a five-minute Monday check replaces an hour spent scrolling through P6.</p>
            <div className="beat-points">
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                Activities grouped by what's starting, ongoing, and finishing
              </div>
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                Float-based risk badges — critical, on watch, on track
              </div>
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                Update actual dates directly from the field, from a phone
              </div>
            </div>
          </div>
          <div className="beat-visual">
            <div className="frame">
              <div className="frame-chrome">
                <div className="frame-dot red"></div>
                <div className="frame-dot amber"></div>
                <div className="frame-dot green"></div>
                <div className="frame-url">{BRAND_NAME} / lookahead</div>
              </div>
              <div className="app-nav-mini">
                <div className="app-nav-mini-brand">
                  <LogoMark variant="onDark" height={12} />
                  {BRAND_NAME}
                </div>
                <div className="app-nav-mini-tabs">
                  <div className="app-nav-mini-tab active">Lookahead</div>
                  <div className="app-nav-mini-tab">Ask</div>
                  <div className="app-nav-mini-tab">Reports</div>
                </div>
              </div>
              <div className="mini-scroll">
                <table className="mini-table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Finish</th>
                      <th>Float</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>As-Builts Drawings</td>
                      <td className="secondary">Apr 29</td>
                      <td className="secondary">0d</td>
                      <td><span className="badge badge-red">Critical</span></td>
                    </tr>
                    <tr>
                      <td>Inspect Building 4 Systems</td>
                      <td className="secondary">Jul 5</td>
                      <td className="secondary">8d</td>
                      <td><span className="badge badge-amber">On watch</span></td>
                    </tr>
                    <tr>
                      <td>Fire Alarm Commissioning</td>
                      <td className="secondary">Jul 8</td>
                      <td className="secondary">3d</td>
                      <td><span className="badge badge-amber">On watch</span></td>
                    </tr>
                    <tr>
                      <td>Landscaping — Phase 2</td>
                      <td className="secondary">Jul 19</td>
                      <td className="secondary">26d</td>
                      <td><span className="badge badge-green">On track</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6 — WHAT-IF BEAT */}
      <section className="section" id="whatif">
        <div className="beat-row reverse">
          <div className="beat-text">
            <div className="beat-eyebrow">Impact analysis</div>
            <h3 className="beat-title">Don't find out after you make the change.</h3>
            <p className="beat-body">Before you commit a schedule change, Flotillo runs it through the same CPM engine driving the live schedule and shows you the consequence — new completion date, which activities shift, what goes critical — before it touches P6 or your stored schedule.</p>
            <div className="beat-points">
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                Preview the completion-date shift before you save
              </div>
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                See exactly which activities move, and which go critical
              </div>
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                Decide with the consequence in front of you, not after
              </div>
            </div>
          </div>
          <div className="beat-visual">
            <div className="frame">
              <div className="frame-chrome">
                <div className="frame-dot red"></div>
                <div className="frame-dot amber"></div>
                <div className="frame-dot green"></div>
                <div className="frame-url">{BRAND_NAME} / lookahead</div>
              </div>
              <div className="impact-panel">
                <div className="impact-header">Impact Preview</div>
                <div className="impact-change">Change: <b>As-Builts Drawings</b> — actual finish → Aug 14</div>
                <div className="impact-date-card">
                  <div>
                    <div className="impact-date-label">Previous completion</div>
                    <div className="impact-date-value muted">Jan 5, 2027</div>
                  </div>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--faint)" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M13 6l6 6-6 6M5 12h14"/></svg>
                  <div>
                    <div className="impact-date-label">Updated completion</div>
                    <div className="impact-date-value">Jan 17, 2027</div>
                  </div>
                  <span className="badge badge-red">+12d</span>
                </div>
                <div className="impact-list">
                  <div className="impact-list-title">This changes</div>
                  <div className="impact-list-item">3 activities shift onto the critical path</div>
                  <div className="impact-list-item">Final Acceptance &amp; Handover moves out 12 days</div>
                  <div className="impact-list-item">Fire Alarm Commissioning float drops to 3 days</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7 — ASK AI BEAT */}
      <section className="section alt" id="ask">
        <div className="beat-row">
          <div className="beat-text">
            <div className="beat-eyebrow">Schedule questions</div>
            <h3 className="beat-title">Ask a question. Get a real answer.</h3>
            <p className="beat-body">Type the question you'd actually ask your scheduler — in plain English — and get an answer sourced directly from your live schedule data, not a canned summary.</p>
            <div className="beat-points">
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                No jargon, no navigating a schedule network by hand
              </div>
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                Answers reference the actual activities driving the answer
              </div>
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                Available to anyone with access — not just the scheduler
              </div>
            </div>
          </div>
          <div className="beat-visual">
            <div className="frame">
              <div className="frame-chrome">
                <div className="frame-dot red"></div>
                <div className="frame-dot amber"></div>
                <div className="frame-dot green"></div>
                <div className="frame-url">{BRAND_NAME} / ask</div>
              </div>
              <div className="chat-panel">
                <div className="chat-bubble user">Which activities are driving our completion date right now?</div>
                <div className="chat-bubble answer">
                  Two activities are driving your completion date: <b>As-Builts Drawings</b> (0 days float, blocking Final Acceptance) and <b>Fire Alarm Commissioning</b> (3 days float). Recovering As-Builts by two weeks would pull completion back to your contract date.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8 — REPORTING BEAT */}
      <section className="section" id="reporting">
        <div className="beat-row reverse">
          <div className="beat-text">
            <div className="beat-eyebrow">Reporting</div>
            <h3 className="beat-title">Turn schedule updates into something you can communicate.</h3>
            <p className="beat-body">Every week's update — real dates, real float, real risk — turns into a written status report ready to send, without starting from a blank page.</p>
            <div className="beat-points">
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                Drafted straight from your current schedule data
              </div>
              <div className="beat-point">
                <div className="beat-point-check"><svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg></div>
                Edit the narrative, then send or download as PDF
              </div>
            </div>
          </div>
          <div className="beat-visual">
            <div className="frame">
              <div className="frame-chrome">
                <div className="frame-dot red"></div>
                <div className="frame-dot amber"></div>
                <div className="frame-dot green"></div>
                <div className="frame-url">{BRAND_NAME} / reports</div>
              </div>
              <div className="report-demo">
                <div className="report-top">
                  <div>
                    <div className="report-title">Weekly Status Report</div>
                    <div className="report-date">Week ending June 30, 2026</div>
                  </div>
                  <div className="report-ai-tag">Drafted for you</div>
                </div>
                <div className="report-body">
                  The project is currently tracking 12 days behind its contractual completion of January 5, 2027. The primary driver is As-Builts Drawings — 0 days float and on the critical path. Recovering this activity by mid-July would pull completion back to the contract date.
                </div>
                <div className="report-actions">
                  <div className="report-btn primary">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    Send to owner
                  </div>
                  <div className="report-btn">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download PDF
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9 — WHO IT'S FOR */}
      <section className="section alt" id="for">
        <div className="section-eyebrow">Who it's for</div>
        <h2 className="section-title tight">Built for the people who manage the project — not just the schedule.</h2>
        <div className="card-grid">
          <div className="info-card">
            <div className="info-icon green">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M2 20h20M4 20V10l8-7 8 7v10"/><path d="M9 20v-6h6v6"/></svg>
            </div>
            <div className="info-title">Project Managers</div>
            <div className="info-body">Update actual dates, spot risk before it becomes a problem, and draft owner reports in minutes instead of an afternoon.</div>
          </div>
          <div className="info-card">
            <div className="info-icon green">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <div className="info-title">Superintendents</div>
            <div className="info-body">See what's starting, finishing, and on the critical path from a phone — no schedule network to navigate.</div>
          </div>
          <div className="info-card">
            <div className="info-icon green">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0-3-3.87"/></svg>
            </div>
            <div className="info-title">Executives &amp; Operations</div>
            <div className="info-body">Portfolio-wide visibility into risk and completion dates, without a P6 seat for every project lead.</div>
          </div>
        </div>
      </section>

      {/* 10 — WORKS ALONGSIDE P6 */}
      <section className="section" id="approach">
        <div className="approach-block">
          <div className="section-eyebrow">Works alongside P6</div>
          <h2 className="section-title tight">Your scheduler keeps the P6 baseline. Your PM keeps the project moving.</h2>
          <p>Flotillo gives PMs a simpler environment for day-to-day schedule updates and analysis, while keeping P6 in the workflow.</p>
          <div className="approach-tag">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M5 13l4 4L19 7"/></svg>
            No additional P6 license required for PMs
          </div>
        </div>
      </section>

      {/* 11 — FINAL CTA */}
      <section className="final">
        <h2 className="final-title">Stop spending Monday interpreting P6.</h2>
        <p className="final-sub">Start with the schedule you already have. See what Flotillo finds.</p>
        <div className="hero-btns">
          <button onClick={startDemo} disabled={demoLoading} className="btn-primary" style={{fontSize:'15px',padding:'13px 28px'}}>
            {demoLoading ? 'Loading sample schedule…' : 'Explore the live demo'}
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
        <p className="final-note">Opens a sample project. No upload or signup.</p>
      </section>

      {/* FOOTER */}
      <footer className="footer">
        <div className="footer-logo"><Logo variant="onDark" wordmarkSize={17} /></div>
        <div className="footer-links">
          <a href="#">Privacy</a>
          <a href="#">Terms</a>
          <a href="#">Contact</a>
        </div>
      </footer>

      <RequestAccessModal open={requestAccessOpen} onClose={() => setRequestAccessOpen(false)} />
    </>
  );
}
